import React, { useState, useRef } from "react";
import axios from "axios";
import { Toast } from "primereact/toast";
import SvgCreatRecruiter from "./SvgCreatRecruiter.jsx";
import colors from "colors/color";
import { useTranslation } from "react-i18next";

import {
  TextField,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Container,
  Typography,
  Dialog,
  DialogContent,
  Box,
  IconButton,
  Snackbar,
  Grid,
  Paper,
  Avatar,
  Divider,
  Alert,
  Card,
  CardContent,
  styled,
  alpha,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { LinearProgress } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EmailIcon from '@mui/icons-material/Email';
import WcIcon from '@mui/icons-material/Wc';
import DomainIcon from '@mui/icons-material/Domain';
import DescriptionIcon from '@mui/icons-material/Description';
import { CloudUpload } from "lucide-react";
import GlobalCityAutocomplete from "components/CitiesPicker/CitiesPicker.jsx";
import CategorySelectorDemo from "./CategorySelectorDemo.jsx";
import { OutlinedButton } from "components/Buttons/Buttons.jsx";
import { NewCreateOfferButton } from "components/Buttons/Buttons.jsx";
import SmartHireLoader from "components/Loading/SmartHireLoader.jsx";

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: "#64748b",
  fontSize: "14px",
  fontFamily: "Poppins",
  fontWeight: 500,
  mb: 1,
}));

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: 1000,
  margin: 'auto',
  marginTop: theme.spacing(3),
  borderRadius: 20,
  boxShadow: `0 20px 60px -12px ${alpha(colors.primary, 0.15)}`,
  background: 'linear-gradient(135deg, #ffffff 0%, #fafbff 100%)',
  border: `1px solid ${alpha(colors.primary, 0.08)}`,
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: `2px solid ${alpha(colors.primary, 0.1)}`,
  background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
  boxShadow: `0 8px 32px ${alpha(colors.primary, 0.08)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 40px ${alpha(colors.primary, 0.12)}`,
    border: `2px solid ${alpha(colors.primary, 0.2)}`,
  },
}));

const StyledButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1rem',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  ...(variant === 'contained' && {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${alpha(colors.primary, 0.8)} 100%)`,
    boxShadow: `0 8px 25px ${alpha(colors.primary, 0.3)}`,
    '&:hover': {
      background: `linear-gradient(135deg, ${alpha(colors.primary, 0.9)} 0%, ${colors.primary} 100%)`,
      boxShadow: `0 12px 35px ${alpha(colors.primary, 0.4)}`,
      transform: 'translateY(-2px)',
    },
  }),
  ...(variant === 'outlined' && {
    borderColor: colors.secondary,
    color: colors.primary,
    borderWidth: 2,
    '&:hover': {
      borderColor: colors.primary,
      backgroundColor: alpha(colors.primary, 0.04),
      transform: 'translateY(-1px)',
    },
  }),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: '#ffffff',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: `0 4px 20px ${alpha(colors.primary, 0.08)}`,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 3px ${alpha(colors.primary, 0.1)}`,
      '& fieldset': {
        borderColor: colors.primary,
        borderWidth: 2,
      },
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: colors.primary,
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: '#ffffff',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: `0 4px 20px ${alpha(colors.primary, 0.08)}`,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 3px ${alpha(colors.primary, 0.1)}`,
      '& fieldset': {
        borderColor: colors.primary,
        borderWidth: 2,
      },
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: colors.primary,
  },
}));

// Modern Stepper Components
const ModernStepperContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3, 0),
}));

const StepperWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  width: '100%',
});

const StepContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: 1,
  position: 'relative',
  '&:first-of-type': {
    alignItems: 'flex-start',
  },
  '&:last-child': {
    alignItems: 'flex-end',
  },
});

const StepCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'status',
})(({ theme, status }) => {
  const baseStyles = {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '1rem',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    zIndex: 2,
  };

  if (status === 'completed') {
    return {
      ...baseStyles,
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: '#ffffff',
      boxShadow: `0 8px 25px ${alpha('#10b981', 0.4)}`,
      transform: 'scale(1.05)',
    };
  } else if (status === 'active') {
    return {
      ...baseStyles,
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${alpha(colors.primary, 0.8)} 100%)`,
      color: '#ffffff',
      boxShadow: `0 0 0 4px ${alpha(colors.primary, 0.15)}, 0 8px 25px ${alpha(colors.primary, 0.3)}`,
      transform: 'scale(1.1)',
    };
  } else {
    return {
      ...baseStyles,
      backgroundColor: '#f3f4f6',
      color: '#9ca3af',
      border: '2px solid #e5e7eb',
    };
  }
});

const StepLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'status',
})(({ theme, status }) => {
  const baseStyles = {
    marginTop: theme.spacing(1.5),
    fontSize: '0.875rem',
    fontWeight: 500,
    textAlign: 'center',
    transition: 'color 0.3s ease',
    fontFamily: 'Poppins',
  };

  if (status === 'completed') {
    return {
      ...baseStyles,
      color: '#10b981',
    };
  } else if (status === 'active') {
    return {
      ...baseStyles,
      color: colors.primary,
      fontWeight: 600,
    };
  } else {
    return {
      ...baseStyles,
      color: '#9ca3af',
    };
  }
});

const ConnectorLine = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isCompleted',
})(({ theme, isCompleted }) => ({
  position: 'absolute',
  top: 28, // Center vertically with the circle (56px height / 2 = 28px)
  left: 'calc(50% + 28px)', // Start after the current circle (28px = half circle width)
  width: 'calc(100% - 56px)', // Full width minus both circle widths (28px * 2)
  height: 4,
  borderRadius: 2,
  transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
  background: isCompleted 
    ? `linear-gradient(90deg, ${colors.primary} 0%, #10b981 100%)`
    : '#e2e8f0',
  zIndex: 1,
  transform: isCompleted ? 'scaleX(1)' : 'scaleX(1)',
  transformOrigin: 'left',
  opacity: isCompleted ? 1 : 0.3,
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const ModernStepper = ({ activeStep, steps }) => {
  const getStepStatus = (stepIndex) => {
    if (stepIndex < activeStep) return 'completed';
    if (stepIndex === activeStep) return 'active';
    return 'pending';
  };

  return (
    <ModernStepperContainer>
      <StepperWrapper>
        {steps.map((label, index) => {
          const status = getStepStatus(index);
          const isLast = index === steps.length - 1;
          const hasNextStep = index < steps.length - 1;
          
          return (
            <StepContainer key={label}>
              <StepCircle status={status}>
                {status === 'completed' ? (
                  <CheckCircleIcon sx={{ fontSize: 24 }} />
                ) : (
                  index + 1
                )}
              </StepCircle>
              
              <StepLabel status={status}>
                {label}
              </StepLabel>
              
              {/* Only show connector line if there's a next step */}
              {hasNextStep && (
                <ConnectorLine isCompleted={index < activeStep} />
              )}
            </StepContainer>
          );
        })}
      </StepperWrapper>
    </ModernStepperContainer>
  );
};

const generateRandomPassword = () => {
  const length = 12;
  const charset =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=";
  let password = "";
  for (let i = 0, n = charset.length; i < length; ++i) {
    password += charset.charAt(Math.floor(Math.random() * n));
  }
  return password;
};

function getUserIdFromAccessToken() {
  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) {
    console.error("No access token found in local storage");
    return null;
  }

  try {
    const decodedToken = parseJwt(accessToken);
    //console.log("Access token : ", decodedToken);
    const senderId = decodedToken.user_id;
    return senderId;
  } catch (error) {
    console.error("Failed to decode access token", error);
    return null;
  }
}

function parseJwt(token) {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );
  //console.log("payload : ", jsonPayload);

  return JSON.parse(jsonPayload);
}

const cities = [
  "Casablanca",
  "Rabat",
  "Marrakesh",
  "Fes",
  "Tangier",
  "Agadir",
  "Meknes",
  "Oujda",
  "Kenitra",
  "Tetouan",
  "Safi",
  "El Jadida",
  "Nador",
  "Khouribga",
  "Beni Mellal",
  "Mohammedia",
  "Laayoune",
  "Khemisset",
  "Settat",
  "Sidi Kacem",
  "Taza",
  "Larache",
  "Berkane",
  "Errachidia",
  "Taroudant",
  "Guelmim",
  "Ouarzazate",
  "Essaouira",
  "Dakhla",
  "Azrou",
  "Oued Zem",
  "Fqih Ben Salah",
];
const apiUrl = process.env.REACT_APP_API_URL;
const apiUrlsoket = process.env.REACT_APP_API_URL_SOKET;

const CreateRecruiter = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: "",
    first_name: "",
    last_name: "",
    email: "",
    //  password: "",
    gender: "",
    company: "",
    address: "",
    city: "",
    country: "",
    postal_code: "",
    comp_industry: "",
    comp_description: "",
    image: null,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [errors, setErrors] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const toast = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData((prevState) => ({
        ...prevState,
        image: file,
      }));
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setFormData((prevState) => ({
      ...prevState,
      image: null,
    }));
    setPreviewImage(null);
  };
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const generatedPassword = generateRandomPassword();
    const adminId = getUserIdFromAccessToken();

    try {
      const response = await fetch(
        `${apiUrl}recruiters/create_recruiter_admin/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...formData,
            password: generatedPassword,
            adminId: adminId,
          }),
        }
      );

      if (response.ok) {
        // Show success dialog immediately
        setOpenDialog(true);
        setOpenSnackbar(true);
        // toast.current.show({ 
        //   severity: "success",
        //   summary: "Success",
        //   detail: "Recruiter Created Successfully!",
        //   life: 3000,
        // });

        // Reset form
        setFormData({
          username: "",
          first_name: "",
          last_name: "",
          email: "",
          gender: "",
          company: "",
          address: "",
          city: "",
          country: "",
          postal_code: "",
          comp_industry: "",
          comp_description: "",
          image: null,
        });
        setPreviewImage(null);

        // Close the dialog after 3 seconds
        setTimeout(() => {
          setOpenDialog(false);
        }, 3000);
      } else {
        const errorData = await response.json();
        toast.current.show({
          severity: "error",
          summary: t('createRecruiter.messages.error'),
          detail: errorData.error || t('createRecruiter.messages.serverError', { status: response.status }),
          life: 3000,
        });
      }
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: t('createRecruiter.messages.error'),
        detail: error.message || t('createRecruiter.messages.failedToCreate'),
        life: 3000,
      });
      console.error("Error creating recruiter:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const steps = [t('createRecruiter.steps.personalInformation'), t('createRecruiter.steps.location')];
  const [activeStep, setActiveStep] = React.useState(0);
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  const handleImageUpload = (event) => {
    // Implement image upload logic
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* <StyledPaper elevation={0}> */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 4,
            p: 3,
            // background: `linear-gradient(135deg, ${alpha(colors.primary, 0.05)} 0%, ${alpha(colors.primary, 0.02)} 100%)`,
            // borderRadius: 16,
            // border: `1px solid ${alpha(colors.primary, 0.1)}`,
          }}
        >
          <Avatar 
            sx={{ 
              bgcolor: colors.primary,
              width: 60,
              height: 60,
              mr: 3,
              // boxShadow: `0 8px 25px ${alpha(colors.primary, 0.3)}`,
            }}
          >
            <PersonAddIcon sx={{ fontSize: 30 }} />
          </Avatar>
          <Box>
            <Typography 
              variant="h3" 
              component="h1"
              sx={{
                fontWeight: 700,
                // background: `linear-gradient(135deg, ${colors.primary} 0%, ${alpha(colors.primary, 0.7)} 100%)`,
                background: colors.primary,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
                fontFamily: 'Poppins',           }}
            >
              {t('createRecruiter.title')}
            </Typography>
            <Typography 
              sx={{ 
                color: alpha(colors.primary, 0.7),
                fontWeight: 500,
                fontFamily:"Poppins"
              }}
            >
              {t('createRecruiter.subtitle')}
            </Typography>
          </Box>
        </Box>

      <ModernStepper activeStep={activeStep} steps={steps} />

      <form onSubmit={(e) => {
        e.preventDefault();
        if (activeStep === steps.length - 1) {
          handleSubmit(e);
        } else {
          handleNext();
        }
      }}>
        {activeStep === 0 && (
          <Box>
            <Typography  sx={{ mb: 2, display: 'flex', alignItems: 'center', fontFamily:'Poppins',textTransform:'none', fontSize:18 }}>
             {t('createRecruiter.steps.personalInformation')}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <StyledTypography>{t('createRecruiter.fields.firstName')}</StyledTypography>
                <StyledTextField
                  variant="outlined"
                  // label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <StyledTypography>{t('createRecruiter.fields.lastName')}</StyledTypography>
                <StyledTextField
                  variant="outlined"
                  // label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTypography>{t('createRecruiter.fields.email')}</StyledTypography>
                <StyledTextField
                  variant="outlined"
                  // label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTypography>{t('createRecruiter.fields.gender')}</StyledTypography>

                <StyledFormControl fullWidth required>
                  {/* <InputLabel id="gender-label">Gender</InputLabel> */}
                  <Select
                    labelId="gender-label"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    // label="Gender"
                    startAdornment={
                      <WcIcon 
                        sx={{ 
                          color: alpha(colors.primary, 0.5),
                          mr: 1,
                          fontSize: 20,
                        }} 
                      />
                    }
                  >
                    <MenuItem value="f">{t('createRecruiter.genderOptions.mr')}</MenuItem>
                    <MenuItem value="m">{t('createRecruiter.genderOptions.ms')}</MenuItem>
                  </Select>
                </StyledFormControl>
              </Grid>

            </Grid>
          </Box>
        )}

        {/* {activeStep === 1 && (
          <Box>
            <Typography  sx={{ mb: 2, display: 'flex', alignItems: 'center', fontFamily:'Poppins',textTransform:'none', fontSize:18 }}>
              {t('createRecruiter.steps.companyDetails')}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <StyledTypography>{t('createRecruiter.fields.company')}</StyledTypography>

                <StyledTextField
                  variant="outlined"
                  // label="Company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTypography>{t('createRecruiter.fields.industry')}</StyledTypography>
                <StyledTextField
                  variant="outlined"
                  // label=""
                  name="comp_industry"
                  value={formData.comp_industry}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTypography>{t('createRecruiter.fields.companyDescription')}</StyledTypography>
                <StyledTextField
                  variant="outlined"
                  // label="Company Description"
                  name="comp_description"
                  value={formData.comp_description}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  fullWidth
                  placeholder={t('createRecruiter.placeholders.companyDescription')}
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )} */}

        {/* {activeStep === 1 && ( */}
          {/* <Box>
            <Typography  sx={{ mb: 2, display: 'flex', alignItems: 'center', fontFamily:'Poppins',textTransform:'none', fontSize:18 }}>
               {t('createRecruiter.steps.location')}
            </Typography>
            <Grid container spacing={3}>

              <Grid item xs={12} sm={6}>
                {/* <StyledTypography>Location</StyledTypography> *
                <GlobalCityAutocomplete
                  sx={{ borderColor: "#e0e0e0" }}
                  label={t('createRecruiter.fields.postalCode')}
                  value={formData.country}
                  onChange={(newValue) => {
                    setFormData(prevState => ({
                      ...prevState,
                      country: newValue
                    }));
                    setErrors((prev) => ({ ...prev, location: "" }));
                  }}
                // error={!!errors?.location}
                // helperText={errors?.location}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTypography>{t('createRecruiter.fields.postalCode')}</StyledTypography>
                <StyledTextField
                  variant="outlined"
                  // label=""
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTypography>{t('createRecruiter.fields.address')}</StyledTypography>
                <StyledTextField
                  variant="outlined"
                  // label=""
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )} */}

          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              mt: 5,
              pt: 3,
              borderTop: `1px solid ${alpha(colors.secondary, 0.3)}`,
            }}
          >
            <OutlinedButton
              disabled={activeStep === 0}
              onClick={handleBack}
              // variant="outlined"
              sx={{
                visibility: activeStep === 0 ? 'hidden' : 'visible',
              }}
            >
              {t('createRecruiter.buttons.back')}
            </OutlinedButton>
            
            <NewCreateOfferButton
              type="submit"
              // variant="contained"
              disabled={isLoading}
              // sx={{
              //   minWidth: 140,
              //   position: 'relative',
              // }}
            >
              {isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress 
                    sx={{ 
                      width: 20, 
                      height: 2,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#ffffff',
                      },
                    }} 
                  />
                  <SmartHireLoader/>
                </Box>
              ) : (
                activeStep === steps.length - 1 ? t('createRecruiter.buttons.createRecruiter') : t('createRecruiter.buttons.next')
              )}
            </NewCreateOfferButton>
          </Box>
        </form>
        <Snackbar
          open={openSnackbar}
          autoHideDuration={4000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert 
            onClose={() => setOpenSnackbar(false)} 
            severity="success" 
            sx={{ 
              width: "100%",
              borderRadius: 2,
              boxShadow: `0 8px 32px ${alpha(colors.primary, 0.15)}`,
              '& .MuiAlert-icon': {
                color: colors.primary,
              },
            }}
          >
            {t('createRecruiter.messages.success')}
          </Alert>
        </Snackbar>

      {/* </StyledPaper> */}
    </Container>
  );
};

export default CreateRecruiter;